import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container,
  Card,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from '@material-ui/core';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import WrapperSeamless from '../../layout-components/WrapperSeamless';
import Table from '../../components/tables/table';
import CustomSelect from '../../components/select';
import * as Api from '../../service';
import useStore from '../../context';

const defaultFilters = {
  q: '',
  status: '',
  tagIds: [],
  categoryIds: [],
  authorId: '',
  publishedFrom: '',
  publishedTo: '',
  popular: false,
  sort: ''
};

const coerceId = (val) => {
  const num = Number(val);
  return Number.isNaN(num) ? val : num;
};

const parseSearch = (search) => {
  const params = new URLSearchParams(search);
  return {
    filters: {
      q: params.get('q') || '',
      status: params.get('status') || '',
      tagIds: params.getAll('tagIds').map(coerceId),
      categoryIds: params.getAll('categoryIds').map(coerceId),
      authorId: params.get('authorId') || '',
      publishedFrom: params.get('publishedFrom') || '',
      publishedTo: params.get('publishedTo') || '',
      popular: params.get('popular') === 'true',
      sort: params.get('sort') || ''
    },
    page: parseInt(params.get('page') || '1', 10)
  };
};

const normalizeList = (res) => {
  if (Array.isArray(res)) {
    return { items: res, total: res.length };
  }
  const items = res?.items || res?.data || res?.results || [];
  const total =
    res?.total ?? res?.totalCount ?? res?.count ?? res?.meta?.total ?? items.length;
  return { items, total };
};

const fields = [
  <th className="text-left px-4" width="240">Title</th>,
  <th className="text-left">Status</th>,
  <th className="text-left">Publish At</th>,
  <th className="text-left">Author</th>,
  <th className="text-left">Views</th>
];

const BlogPosts = () => {
  const history = useHistory();
  const location = useLocation();
  const { setRemove, setErrorMsg, setSuccessMsg } = useStore();

  const [{ filters, page }, setQueryState] = useState(() =>
    parseSearch(location.search)
  );

  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  const queryState = useMemo(() => parseSearch(location.search), [location.search]);

  useEffect(() => {
    setQueryState(queryState);
  }, [queryState]);

  const loadTags = useCallback(() => {
    Api.tags.getAll()
      .then((res) => setTags(Array.isArray(res) ? res : res?.items || []))
      .catch(() => {});
  }, []);

  const loadCategories = useCallback(() => {
    Api.blog.getCategories()
      .then((res) => setCategories(Array.isArray(res) ? res : res?.items || []))
      .catch(() => {});
  }, []);

  const loadPosts = useCallback(async () => {
    const limit = 10;
    const offset = (queryState.page - 1) * limit;
    const apiQuery = {
      ...queryState.filters,
      limit,
      offset
    };
    if (!apiQuery.popular) {
      delete apiQuery.popular;
    }
    if (!apiQuery.sort) {
      delete apiQuery.sort;
    }
    setLoading(true);
    try {
      const res = await Api.blog.getAll(apiQuery);
      const normalized = normalizeList(res);
      setPosts(normalized.items);
      setTotal(normalized.total);
    } catch (e) {
      setErrorMsg(e.message);
    } finally {
      setLoading(false);
    }
  }, [queryState]);

  useEffect(() => {
    loadTags();
    loadCategories();
  }, []);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const updateQuery = (nextFilters, nextPage = 1) => {
    const params = new URLSearchParams();
    if (nextFilters.q) params.set('q', nextFilters.q);
    if (nextFilters.status) params.set('status', nextFilters.status);
    if (nextFilters.authorId) params.set('authorId', nextFilters.authorId);
    if (nextFilters.publishedFrom)
      params.set('publishedFrom', nextFilters.publishedFrom);
    if (nextFilters.publishedTo)
      params.set('publishedTo', nextFilters.publishedTo);
    if (nextFilters.sort) params.set('sort', nextFilters.sort);
    if (nextFilters.popular) params.set('popular', 'true');
    nextFilters.tagIds.forEach((id) => params.append('tagIds', id));
    nextFilters.categoryIds.forEach((id) =>
      params.append('categoryIds', id)
    );
    params.set('page', String(nextPage));
    history.push({ search: params.toString() });
  };

  const handleApply = (e) => {
    e.preventDefault();
    updateQuery(filters, 1);
  };

  const handleClear = () => {
    updateQuery(defaultFilters, 1);
  };

  const publishNow = async (id) => {
    try {
      await Api.blog.publish(id);
      setSuccessMsg('published');
      loadPosts();
    } catch (e) {
      setErrorMsg(e.message);
    }
  };

  const rows = posts?.map((post) => {
    const publishAt =
      post.publishAt ||
      post.publishedAt ||
      post.scheduledAt ||
      post.updatedAt ||
      '';
    return (
      <>
        <td className="text-left">
          <div className="font-size-sm" style={{ width: 'max-content' }}>
            {post.title || post.metaTitle || post.slug}
          </div>
          {post.slug && (
            <div className="font-size-xs text-black-50">{post.slug}</div>
          )}
        </td>
        <td className="text-left">
          <div className="font-size-sm">{post.status || 'DRAFT'}</div>
        </td>
        <td className="text-left">
          <div className="font-size-sm">{publishAt}</div>
        </td>
        <td className="text-left">
          <div className="font-size-sm">
            {post.authorNameOverride || post.authorName || post.author || '-'}
          </div>
        </td>
        <td className="text-left">
          <div className="font-size-sm">
            {post.viewCount ?? post.views ?? '-'}
          </div>
        </td>
        <td className="text-right">
          <Link to={`/blog/create/${post.id || post._id}`}>
            <Button className="btn-neutral-first mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center">
              <FontAwesomeIcon icon={['far', 'edit']} className="font-size-sm" />
            </Button>
          </Link>

          <Button
            disabled={post.status === 'PUBLISHED'}
            onClick={() => publishNow(post.id || post._id)}
            className="btn-neutral-success mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center">
            <FontAwesomeIcon icon={['fas', 'upload']} className="font-size-sm" />
          </Button>

          <Button
            onClick={() =>
              setRemove(() => () =>
                Api.blog.delete(post.id || post._id).then(loadPosts)
              )
            }
            className="btn-neutral-danger mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center">
            <FontAwesomeIcon icon={['fas', 'times']} className="font-size-sm" />
          </Button>
        </td>
      </>
    );
  });

  return (
    <Container maxWidth>
      <WrapperSeamless sectionHeading="Blog Posts">
        <Card className="shadow-xxl px-4 py-3 mb-4">
          <Box component="form" onSubmit={handleApply}>
            <div className="d-flex flex-wrap align-items-center">
              <TextField
                className="my-2 mr-3"
                label="Search"
                variant="outlined"
                value={filters.q}
                onChange={(e) =>
                  setQueryState((st) => ({
                    ...st,
                    filters: { ...st.filters, q: e.target.value }
                  }))
                }
              />
              <FormControl variant="outlined" className="my-2 mr-3">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={filters.status}
                  onChange={(e) =>
                    setQueryState((st) => ({
                      ...st,
                      filters: { ...st.filters, status: e.target.value }
                    }))
                  }>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="DRAFT">DRAFT</MenuItem>
                  <MenuItem value="PUBLISHED">PUBLISHED</MenuItem>
                  <MenuItem value="SCHEDULED">SCHEDULED</MenuItem>
                </Select>
              </FormControl>
              <TextField
                className="my-2 mr-3"
                label="Author ID"
                variant="outlined"
                value={filters.authorId}
                onChange={(e) =>
                  setQueryState((st) => ({
                    ...st,
                    filters: { ...st.filters, authorId: e.target.value }
                  }))
                }
              />
              <TextField
                className="my-2 mr-3"
                label="Published From (ISO)"
                placeholder="2026-02-01T00:00:00Z"
                variant="outlined"
                value={filters.publishedFrom}
                onChange={(e) =>
                  setQueryState((st) => ({
                    ...st,
                    filters: { ...st.filters, publishedFrom: e.target.value }
                  }))
                }
              />
              <TextField
                className="my-2 mr-3"
                label="Published To (ISO)"
                placeholder="2026-02-28T23:59:59Z"
                variant="outlined"
                value={filters.publishedTo}
                onChange={(e) =>
                  setQueryState((st) => ({
                    ...st,
                    filters: { ...st.filters, publishedTo: e.target.value }
                  }))
                }
              />
              <FormControl variant="outlined" className="my-2 mr-3">
                <InputLabel>Sort</InputLabel>
                <Select
                  label="Sort"
                  value={filters.sort}
                  onChange={(e) =>
                    setQueryState((st) => ({
                      ...st,
                      filters: { ...st.filters, sort: e.target.value }
                    }))
                  }>
                  <MenuItem value="">Default</MenuItem>
                  <MenuItem value="publishAt:DESC">Publish At DESC</MenuItem>
                  <MenuItem value="publishAt:ASC">Publish At ASC</MenuItem>
                  <MenuItem value="viewCount:DESC">Views DESC</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" className="my-2 mr-3">
                <InputLabel>Popular</InputLabel>
                <Select
                  label="Popular"
                  value={filters.popular ? 'true' : 'false'}
                  onChange={(e) =>
                    setQueryState((st) => ({
                      ...st,
                      filters: {
                        ...st.filters,
                        popular: e.target.value === 'true'
                      }
                    }))
                  }>
                  <MenuItem value="false">No</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                </Select>
              </FormControl>
            </div>
            <div className="d-flex flex-wrap">
              <div className="mr-3" style={{ minWidth: 260 }}>
                <CustomSelect
                  noCard
                  title="Tags"
                  multiple
                  data={tags || []}
                  value={filters.tagIds}
                  onChange={(val) =>
                    setQueryState((st) => ({
                      ...st,
                      filters: { ...st.filters, tagIds: val }
                    }))
                  }
                />
              </div>
              <div className="mr-3" style={{ minWidth: 260 }}>
                <CustomSelect
                  noCard
                  title="Categories"
                  multiple
                  data={categories || []}
                  value={filters.categoryIds}
                  onChange={(val) =>
                    setQueryState((st) => ({
                      ...st,
                      filters: { ...st.filters, categoryIds: val }
                    }))
                  }
                />
              </div>
            </div>
            <div className="mt-3 d-flex align-items-center">
              <Button type="submit" className="btn-primary mr-2">
                Apply Filters
              </Button>
              <Button type="button" onClick={handleClear} className="btn-neutral-first">
                Clear
              </Button>
            </div>
          </Box>
        </Card>

        <Table
          title="Posts"
          noDrag
          items={posts}
          setItems={setPosts}
          total={total}
          fields={fields}
          rows={rows}
          actions
          createLink="blog/create"
        />
        {loading && (
          <div className="text-center text-black-50 font-size-sm mt-2">
            Loading...
          </div>
        )}
      </WrapperSeamless>
    </Container>
  );
};

export default BlogPosts;
